<template>
  <div>
    <h2 class="site-title">{{ $t("nav.orders") }}</h2>
    <div class="filter-bar">
      <v-row>
        <v-col class="filter-bar--customer">
          <v-text-field
            v-model="phrase"
            prepend-icon="mdi-magnify"
            @input="search"
            @click:clear="search('')"
            clearable
            :label="$t('orders.filter.search')"
          >
          </v-text-field>
        </v-col>
        <v-col v-if="$auth.check('admin')">
          <v-select
            class="user-picker d-inline-flex"
            solo
            :label="this.$t('orders.filter.author')"
            :menu-props="{ top: true, offsetY: true }"
            item-text="email"
            item-value="id"
            v-model="author"
            :items="users"
          >
          </v-select>
          <v-btn
            v-if="author > 0"
            text
            icon
            class="d-inline-flex"
            color="blue-grey darken-3"
            @click="author = 0"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <v-data-table
      locale="pl-PL"
      :headers="headers"
      :items="orders"
      :server-items-length="total"
      :footer-props="{ itemsPerPageOptions: [5, 10, 20, 50] }"
      :loading="loading"
      :options.sync="pagination"
      class="elevation-1 orders"
    >
      <template v-slot:item.number="{ item }">
        <router-link :to="{ name: 'Order View', params: { id: item.id } }">{{
          item.number
        }}</router-link>
      </template>

      <template v-slot:item.thumbnail="{ item }">
        <v-dialog width="800">
          <template v-slot:activator="{ on }">
            <img class="thumbnail" :src="item.thumbnail" v-on="on" />
          </template>
          <project :terraces="item.terraces"></project>
        </v-dialog>
      </template>

      <template v-slot:item.customer="{ item }">
        <v-dialog width="300">
          <template v-slot:activator="{ on }">
            <span class="activator" v-on="on">
              {{ item.customer.email }}
            </span>
          </template>
          <customer :customer="item.customer"></customer>
        </v-dialog>
      </template>

      <template v-slot:item.price="{ item }">
        <span class="price"
          >{{ item.price | priceDecimal | trailingZeros }} {{ item.currency.symbol }}</span
        >
      </template>

      <template v-slot:item.items="{ item }">
        <v-dialog style="max-width: 1200px" width="90%">
          <template v-slot:activator="{ on }">
            <v-btn rounded color="teal" small dark v-on="on">
              <span style="font-weight: bold; font-size: 1.1em">{{
                item.items.lenght
              }}</span>
              <v-icon>mdi-format-list-bulleted</v-icon>
            </v-btn>
          </template>
          <order-items :list="item.items"></order-items>
        </v-dialog>
      </template>

      <template
        v-slot:item.status="{ item }"
        v-if="$auth.check(['admin', 'trader'])"
      >
        <v-edit-dialog
          :return-value.sync="item.status"
          @save="updateStatus"
          @cancel="dialogcancel"
          @open="dialogopen(item)"
          @close="dialogclose"
        >
          {{ $t("order.status." + item.status) }}
          <template v-slot:input>
            <v-select
              :items="statuses"
              label="Edit"
              item-value="id"
              item-text="label"
              v-model="item.status"
              @change="updateParam($event, 'status')"
            >
            </v-select>
          </template>
        </v-edit-dialog>
      </template>
      <template v-slot:item.status="{ item }" v-else>
        <span class="">{{ $t("order.status." + item.status) }}</span>
      </template>
      <template v-slot:item.action="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <order-pdf
              :id="item.id"
              :number="item.number"
              @downloading="loading = true"
              @finished="loading = false"
              v-slot:activator="{ activate }"
            >
              <v-btn
                v-on="{ ...activate, ...on }"
                fab
                small
                color="deep-orange"
              >
                <v-icon color="white"> mdi-file-pdf-box </v-icon>
              </v-btn>
            </order-pdf>
          </template>
          <span>{{ $t("orders.actions.pdf") }}</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-if="item.type == 'manual'"
              v-on="on"
              fab
              small
              color="blue"
              :disabled="loading || !item.new"
              :to="{ name: 'ManualOrderEdit', params: { id: item.id } }"
            >
              <v-icon color="white"> mdi-wrench </v-icon>
            </v-btn>
            <v-btn
              v-else-if="item.type == 'elevation'"
              v-on="on"
              fab
              small
              color="blue"
              :disabled="loading || !item.new"
              :to="{ name: 'ElevationEdit', params: { id: item.id } }"
            >
              <v-icon color="white"> mdi-wrench </v-icon>
            </v-btn>
            <v-btn
              v-else
              v-on="on"
              fab
              small
              color="blue"
              :disabled="loading || !item.new"
              :to="{ name: 'Order Edit', params: { id: item.id } }"
            >
              <v-icon color="white"> mdi-wrench </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("orders.actions.edit") }}</span>
        </v-tooltip>

        <v-tooltip top v-if="$auth.check(['admin', 'trader'])">
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              fab
              small
              color="blue"
              :disabled="loading || firmao.alert"
              @click="exportToFirmao(item.id)"
            >
              <v-icon color="white"> mdi-export </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("orders.actions.firmao") }}</span>
        </v-tooltip>
        <v-tooltip top v-else>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              fab
              small
              color="blue"
              :disabled="loading || !item.new"
              @click="reserveOrder(item.id)"
            >
              <v-icon color="white"> mdi-export </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("orders.actions.reserve") }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              fab
              small
              class="mr-2"
              color="red"
              :disabled="loading || !item.new"
              @click="cancelOrder(item.id)"
            >
              <v-icon color="white"> mdi-cancel </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("orders.actions.cancel") }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-alert v-if="error.alert" type="error">
      {{ error.message }}
    </v-alert>

    <v-alert v-if="firmao.alert" type="warning">
      <span v-if="$auth.check('admin')"
        >{{ this.$t("orders.firmao.alert.admin") }}
        <router-link :to="{ name: 'Settings' }" style="color: white">{{
          $t("nav.settings")
        }}</router-link></span
      >
      <span v-else>{{ $t("orders.firmao.alert.user") }}</span>
    </v-alert>
    <v-snackbar v-model="snack" :timeout="1500" :color="snackColor">
      {{ snackText }}

      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text @click="snack = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Customer from "./orders/Customer";
import Items from "./orders/Items";
import Project from "./orders/Project";
import Pdf from "./orders/Pdf";
import statuses from "@/helpers/orders/statuses";

export default {
  name: "Orders",
  components: {
    Customer,
    Project,
    "order-items": Items,
    "order-pdf": Pdf,
  },
  computed: {
    users() {
      return this.$store.state.users;
    },
    headers() {
      let headers = [
        {
          text: this.$t("base.id"),
          value: "id",
          align: "right",
          sortable: false,
        },
        {
          text: this.$t("orders.number"),
          sortable: false,
          value: "number",
        },
        {
          text: this.$t("orders.company"),
          sortable: false,
          value: "company",
        },
        {
          text: this.$t("orders.created_at"),
          sortable: false,
          value: "created_at",
        },
      ];
      if (this.$auth.check("admin")) {
        headers.push({
          text: this.$t("orders.created_by"),
          value: "created_by",
          sortable: false,
        });
      }
      return headers.concat([
        {
          text: this.$t("orders.project"),
          value: "thumbnail",
          sortable: false,
        },
        {
          text: this.$t("orders.customer"),
          value: "customer",
          sortable: false,
        },
        {
          text: this.$t("orders.totalPrice"),
          value: "price",
          sortable: false,
        },
        {
          text: this.$t("orders.items"),
          value: "items",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("orders.status"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("base.actions"),
          value: "action",
          align: "center",
          sortable: false,
          width: "230px",
        },
      ]);
    },
    statuses() {
      return statuses;
    },
    type() {
      return this.$route.meta.type;
    },
  },
  data() {
    return {
      pagination: {
        page: 1,
        itemsPerPage: 10,
      },
      dialogs: {
        project: false,
        customers: false,
        items: false,
      },
      dialog: false,
      loading: false,
      orders: [],
      total: 0,
      author: 0,
      firmao: {
        alert: false,
      },
      error: {
        alert: false,
        message: "",
      },
      phrase: "",
      editedIndex: -1,
      editedItem: {},
      snack: false,
      snackColor: "",
      snackText: "",
      baseItem: {},
    };
  },
  methods: {
    loadOrders() {
      this.loading = true;
      return new Promise((resolve, reject) => {
        this.error.alert = false;
        let request = `orders?page=${this.pagination.page}&rowsPerPage=${this.pagination.itemsPerPage}`;
        if (this.$route.meta.type) {
          request += `&type=${this.$route.meta.type}`;
        }
        if (this.$auth.check(["admin", "trader"]) && this.author > 0) {
          request += `&created_by=${this.author}`;
        }
        if (this.phrase) {
          request += `&customer=${this.phrase}`;
        }
        this.$http
          .get(request)
          .then(({ data }) => {
            this.loading = false;
            this.orders = data.items;
            this.total = data.total;
            resolve();
          })
          .catch(() => {
            this.loading = false;
            this.error.message = this.$t("orders.errors.load");
            this.error.alert = true;
            reject();
          });
      });
    },
    testFirmao() {
      return new Promise((resolve, reject) => {
        this.$http
          .get("/firmao/check")
          .then(({ data }) => {
            this.firmao.alert = !data.result;
            resolve();
          })
          .catch(() => {
            this.firmao.alert = true;
            reject();
          });
      });
    },
    exportToFirmao(id) {
      this.$http
        .post("/firmao/export/order", { id })
        .then(({ data }) => {
          alert("Migracja udana");
        })
        .catch(() => {
          alert("Niepowodzenie migracji");
        });
    },
    reserveOrder(id) {
      this.$http
        .post("/orders/" + id + "/reserve")
        .then(({ data }) => {
          this.dialogsave(this.$t("order.reserve.success"));
          this.loadOrders();
        })
        .catch(({ message }) => {
          this.dialogcancel(message);
        });
    },
    cancelOrder(id) {
      this.$http
        .post("/orders/" + id + "/cancel")
        .then(({ data }) => {
          this.dialogsave(this.$t("order.cancel.success"));
          this.loadOrders();
        })
        .catch(({ message }) => {
          this.dialogcancel(message);
        });
    },
    search() {
      let value = this.phrase;
      setTimeout(() => {
        if (value === this.phrase) {
          this.loadOrders();
        }
      }, 800);
    },
    dialogsave(text) {
      this.snack = true;
      this.snackColor = "success";
      this.snackText = text ? text : this.$t("order.snack.saved");
    },
    dialogcancel(text) {
      this.snack = true;
      this.snackColor = "error";
      this.snackText = text ? text : this.$t("order.snack.canceled");
    },
    dialogopen(item) {
      this.editedIndex = item.id;
      this.editedItem = Object.assign({}, item);
      this.snack = true;
      this.snackColor = "info";
      this.snackText = this.$t("order.snack.insert");
    },
    dialogclose() {
      this.dialog = false;
    },
    updateParam(event, param) {
      if (this.editedItem) this.editedItem[param] = event;
    },
    updateStatus() {
      this.$http
        .patch("/orders/" + this.editedItem.id + "/status", {
          status: this.editedItem.status,
        })
        .then(({ data }) => {
          this.dialogsave();
          this.dialogclose();
          this.loadOrders();
        })
        .catch(({ message }) => {
          this.dialogcancel(message);
        });
    },
  },
  created() {
    let requests = [this.loadOrders()];
    if (this.$auth.check("admin")) {
      requests.push(this.testFirmao());
      requests.push(this.$store.dispatch("loadUsers"));
    }
    Promise.all(requests)
      .then(() => {
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
      });
  },
  watch: {
    pagination() {
      this.loadOrders();
    },
    author() {
      this.loadOrders();
    },
    type() {
      this.loadOrders();
    },
  },
};
</script>

<style scoped>
.filter-bar {
  text-align: left;
  padding: 10px;
}

.filter-bar--customer {
  max-width: 400px;
}

.user-picker {
  width: 280px;
}

.orders {
  color: #464646;
}

.price {
  font-size: 1.1em;
  font-weight: bold;
}

.thumbnail {
  width: 60px;
  height: 60px;
  object-fit: cover;
  cursor: pointer;
}
</style>
